import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronRight, FolderOpen, Loader2, Lock, Rocket, Search, X } from "lucide-react";
import { baseURL } from "@/utils/appsettings";
import { useAuthToken } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { UpgradeModal } from "@/components/PlanGate";
import EngageChooser from "@/components/engagement/EngageChooser";
import type { EngageServiceKey } from "@/components/engagement/engageServiceCopy";
import { PageHeader, cardClass, primaryButtonClass, secondaryButtonClass } from "@/components/shell/PageHeader";

interface PickerProject {
  id: string;
  name: string;
  type: string;
  status: string;
  thumbnail_url?: string;
}

const SERVICE_KEYS: EngageServiceKey[] = ["development", "migration", "production"];

const typeLabel = (type?: string) =>
  !type || type === "fullstack" ? "Web app" : type === "mobile" ? "Mobile app" : type === "design" ? "Design" : type;

// Full-page version of the "Engage an Expert" flow: choose the project here, then the same embedded configurator the project settings tab uses
const ExpertRequestPage = () => {
  const token = useAuthToken();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { activeWorkspace } = useWorkspace();
  const { isRestricted } = usePlanFeatures();

  const requestedProject = params.get("project");
  const requestedService = SERVICE_KEYS.find((k) => k === params.get("service"));

  const [projects, setProjects] = useState<PickerProject[]>([]);
  const [activeByProject, setActiveByProject] = useState<Set<string>>(new Set());
  const [liveByProject, setLiveByProject] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Remounts the configurator when the project changes so it never carries one project's answers to another
  const [chooserKey, setChooserKey] = useState(0);

  // Same sources the picker modal uses: projects, plus best-effort "Active" and "Live" badges
  useEffect(() => {
    if (!token || !activeWorkspace?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams({ limit: "50", offset: "0", sort_by: "last_modified", sort_order: "desc" });
        q.set("workspace_id", String(activeWorkspace.id));
        const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
        const res = await fetch(`${baseURL}/protected/projects?${q}`, { headers });
        if (!res.ok) throw new Error(`Failed to load projects (${res.status})`);
        const data = await res.json();
        if (!cancelled) {
          setProjects((data.projects || []).map((p: PickerProject) => ({
            id: p.id, name: p.name || p.id, type: p.type, status: p.status, thumbnail_url: p.thumbnail_url,
          })));
        }
        try {
          const reqRes = await fetch(`${baseURL}/engagement/requests`, { headers });
          if (reqRes.ok) {
            const reqs = await reqRes.json();
            if (!cancelled) {
              setActiveByProject(new Set((reqs || [])
                .filter((r: { status: string }) => !["completed", "cancelled"].includes(r.status))
                .map((r: { project_id: string }) => r.project_id)));
            }
          }
        } catch { /* best-effort badge */ }
        try {
          const liveRes = await fetch(`${baseURL}/app-publishing/apps?page=1&limit=100&status_filter=running`, { headers });
          if (liveRes.ok) {
            const live = await liveRes.json();
            if (!cancelled) {
              setLiveByProject(new Set((live?.apps || [])
                .filter((a: { status: string }) => a.status === "running")
                .map((a: { project_id: string }) => a.project_id)));
            }
          }
        } catch { /* best-effort badge */ }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load projects");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, activeWorkspace?.id]);

  const project = projects.find((p) => p.id === requestedProject);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? projects.filter((p) => `${p.name} ${p.type}`.toLowerCase().includes(q)) : projects;
  }, [projects, search]);

  const chooseProject = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("project", id);
    // Keep `returnTo` so switching project doesn't drop the top bar's way back
    setParams(next, { replace: true, state: location.state });
    setChooserKey((k) => k + 1);
    setPickerOpen(false);
    setSearch("");
  };

  const leave = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/expert");
  };

  const restricted = isRestricted("engage_expert");

  return (
    <div className="mx-auto w-full max-w-[1080px]">
      <PageHeader
        eyebrow="Expert help"
        title="Request help with your project"
        description="Choose the project, then the help you need. Review everything before sending your request."
      />

      {restricted ? (
        <section className={`${cardClass} flex flex-col items-center px-6 py-14 text-center`}>
          <span className="grid h-12 w-12 place-items-center rounded-xl bg-selected text-plum">
            <Lock className="h-5 w-5" />
          </span>
          <h2 className="mt-4 font-display text-[18px] font-semibold tracking-[-0.02em] text-foreground">Expert help isn't included in your plan</h2>
          <p className="mt-1.5 max-w-md text-[14px] text-muted-foreground">Upgrade to bring a Joylo engineer into your project.</p>
          <button type="button" onClick={() => setUpgradeOpen(true)} className={`${primaryButtonClass} mt-5`}>
            See upgrade options <ArrowRight className="h-4 w-4" />
          </button>
        </section>
      ) : loading ? (
        <section className={`${cardClass} flex items-center justify-center gap-2 py-14 text-[14px] text-muted-foreground`}>
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your projects…
        </section>
      ) : error ? (
        <section className={`${cardClass} px-6 py-10 text-center text-[14px] text-danger`}>{error}</section>
      ) : !projects.length ? (
        <section className={`${cardClass} flex flex-col items-center px-6 py-14 text-center`}>
          <span className="grid h-12 w-12 place-items-center rounded-xl bg-selected text-plum">
            <FolderOpen className="h-5 w-5" />
          </span>
          <h2 className="mt-4 font-display text-[18px] font-semibold tracking-[-0.02em] text-foreground">Create a project first</h2>
          <p className="mt-1.5 text-[14px] text-muted-foreground">Expert help is connected to a project in this workspace.</p>
          <Link to="/~" className={`${primaryButtonClass} mt-5`}>
            Start a project <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      ) : (
        <>
          {/* Which project this request is for */}
          <section className={`${cardClass} flex flex-col gap-4 p-5 sm:flex-row sm:items-center`}>
            {project ? (
              <>
                <span className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl bg-selected font-display text-[22px] font-semibold text-plum">
                  {project.thumbnail_url ? <img src={project.thumbnail_url} alt="" className="h-full w-full object-cover" /> : project.name.trim().charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="text-[12px] font-medium text-muted-foreground">You're requesting help for</span>
                  <h2 className="truncate font-display text-[20px] font-semibold tracking-[-0.02em] text-foreground">{project.name}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
                    <span>{activeWorkspace?.name}</span>
                    <span aria-hidden="true">·</span>
                    <span>{typeLabel(project.type)}</span>
                    {liveByProject.has(project.id) && (
                      <span className="rounded-full border border-success/25 bg-success-bg px-2 py-0.5 font-semibold text-success">Live</span>
                    )}
                    {activeByProject.has(project.id) && (
                      <span className="rounded-full border border-plum/25 bg-selected px-2 py-0.5 font-semibold text-plum">Active engagement</span>
                    )}
                  </div>
                </div>
                <button type="button" onClick={() => setPickerOpen((v) => !v)} className={secondaryButtonClass}>
                  {pickerOpen ? "Keep this project" : "Change project"}
                </button>
              </>
            ) : (
              <>
                <div className="min-w-0 flex-1">
                  <span className="text-[12px] font-medium text-muted-foreground">First, choose your project</span>
                  <h2 className="font-display text-[20px] font-semibold tracking-[-0.02em] text-foreground">Which app would you like help with?</h2>
                  <p className="mt-1 text-[13px] text-muted-foreground">
                    {requestedProject
                      ? "That project isn't available in this workspace. Choose a project to continue."
                      : "Your request and the engineer's work will stay with this project."}
                  </p>
                </div>
                {!pickerOpen && (
                  <button type="button" onClick={() => setPickerOpen(true)} className={primaryButtonClass}>
                    Choose project <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </>
            )}
          </section>

          {/* Inline project picker */}
          {(pickerOpen || !project) && (
            <section className={`${cardClass} mt-3 p-4`} aria-label="Choose a project">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    autoFocus={pickerOpen}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search your projects…"
                    className="h-10 w-full rounded-lg border border-input bg-surface pl-9 pr-3 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-plum/40 focus:outline-none"
                  />
                </div>
                {project && (
                  <button type="button" onClick={() => setPickerOpen(false)} aria-label="Close project list" className="grid h-10 w-10 place-items-center rounded-lg text-muted-foreground hover:bg-soft hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="mt-3 max-h-[340px] divide-y divide-line overflow-y-auto rounded-xl border border-line">
                {filtered.length === 0 ? (
                  <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">No projects match your search.</p>
                ) : (
                  filtered.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => chooseProject(p.id)}
                      className={`group flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors hover:bg-soft ${p.id === project?.id ? "bg-selected" : ""}`}
                    >
                      <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg border border-line bg-soft">
                        {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <Rocket className="h-4 w-4 text-muted-foreground" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-semibold text-foreground">{p.name}</span>
                        <span className="block truncate text-[12px] text-muted-foreground">{typeLabel(p.type)}</span>
                      </span>
                      {liveByProject.has(p.id) && (
                        <span className="rounded-full bg-success-bg px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-success">Live</span>
                      )}
                      {activeByProject.has(p.id) && (
                        <span className="rounded-full bg-selected px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-plum">Active</span>
                      )}
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-plum" />
                    </button>
                  ))
                )}
              </div>
            </section>
          )}

          {/* The request itself: the same embedded configurator as the project settings Engage tab */}
          {project && !pickerOpen && (
            <section className={`${cardClass} mt-4 overflow-hidden`}>
              <EngageChooser
                key={`${project.id}-${chooserKey}`}
                embedded
                open
                projectId={project.id}
                isMobile={project.type === "mobile"}
                initialService={requestedService}
                onOpenChange={(v) => { if (!v) leave(); }}
                onEngaged={() => navigate(`/projects/${project.id}`)}
              />
            </section>
          )}
        </>
      )}

      {upgradeOpen && <UpgradeModal feature="engage_expert" isOpen onClose={() => setUpgradeOpen(false)} />}
    </div>
  );
};

export default ExpertRequestPage;
